import { CopyField, Label } from '@teach/ui'
import type { AccessType } from '@/features/instructions/types'
import type { CourseWithoutBlocks } from '@/types/course'
import { CategoryPicker } from '../shared/CategoryPicker'
import { AccessSelect } from './AccessSelect'
import { ExploreToggle } from './ExploreToggle'
import { PlatformVisibility } from './PlatformVisibility'
import { useLiveCourseSlug } from './useLiveCourseSlug'
import { useMessages } from '@/i18n'
import { messages } from './messages'

// ?? трактует '' как «задано» — VITE_ACADEMY_URL= в .env.example даёт '', поэтому || .
const ACADEMY_URL: string = import.meta.env.VITE_ACADEMY_URL || ''
// Бросать здесь нельзя: модуль статически импортируется от App.tsx, это один бандл —
// стенд, которому CI не положил переменную, получил бы белый экран во всём приложении.
// Подставлять window.location.origin тоже нельзя: это ссылка внутрь студии, выданная за
// публичный адрес курса. Без переменной блок ссылки просто не показываем.
if (!ACADEMY_URL) {
  console.warn('VITE_ACADEMY_URL не задан — ссылка на курс не показывается')
}

interface Props {
  access: AccessType
  onAccessChange: (next: AccessType) => void
  categoryIds: number[]
  onCategoryIdsChange: (next: number[]) => void
  /** Нет прав или идёт запрос — гасит секцию целиком. */
  disabled: boolean
  /**
   * Гейт публикации (пустой видео-блок, п.19). Запрещает РОВНО повышение
   * открытости: разделы каталога и понижение доступа остаются живыми —
   * иначе разовый сетевой сбой на одном уроке запирал бы и уменьшение
   * доступа, которое гейту не противоречит. Отсчёт `429` и проверка тоже
   * входят сюда (складывает вызывающий).
   */
  raiseBlocked: boolean
  /** Гейт переключателей платформ — ТОЛЬКО пустое видео. `isHiddenOn*` —
   *  сквозные поля (проверка их не замораживает), а у админа нет антиспама,
   *  поэтому ни проверка, ни отсчёт `429` из `raiseBlocked` сюда не входят. */
  platformBlocked: boolean
  /** Гасит ТОЛЬКО селект доступа (курс на проверке); разделы остаются живыми. */
  selectLocked: boolean
  catalogAllowed: boolean
  retryLabel: string | null
  /** `isHiddenOn*` — рычаг админа, авторам переключатели платформ не показываем. */
  isAdmin: boolean
  noPermission: boolean
  course: CourseWithoutBlocks
  /** Плашка про экспериментальный доступ правее селекта (страница настроек). */
  noteBeside?: boolean
  /** Закрытый курс без названия или описания: селект погашен с этой подсказкой. */
  unfilledHint?: string
}

export const AccessSection = ({
  access,
  onAccessChange,
  categoryIds,
  onCategoryIdsChange,
  disabled,
  raiseBlocked,
  platformBlocked,
  selectLocked,
  catalogAllowed,
  retryLabel,
  isAdmin,
  noPermission,
  course,
  noteBeside = false,
  unfilledHint,
}: Props) => {
  const m = useMessages(messages)
  const slug = useLiveCourseSlug(course)
  const courseUrl = ACADEMY_URL && slug ? `${ACADEMY_URL}/library/${slug}` : null

  return (
    <div className="space-y-6">
      <AccessSelect
        access={access}
        onAccessChange={onAccessChange}
        disabled={disabled || selectLocked}
        raiseBlocked={raiseBlocked}
        catalogAllowed={catalogAllowed}
        retryLabel={retryLabel}
        label={m.courseAccess}
        showTmaStub
        noteBeside={noteBeside}
        unfilledHint={unfilledHint}
      />

      {access === 'public' ? (
        <div className="space-y-2">
          <Label className="text-sm font-medium">{m.catalogSection}</Label>
          <CategoryPicker
            value={categoryIds}
            onChange={onCategoryIdsChange}
            disabled={disabled}
            noPermission={noPermission}
          />
        </div>
      ) : null}

      {access === 'link' ? <ExploreToggle course={course} disabled={disabled} /> : null}

      {access !== 'private' && courseUrl !== null ? (
        <div className="space-y-2">
          <Label className="text-sm font-medium">{m.courseLink}</Label>
          <CopyField value={courseUrl} toastText={m.linkCopied} />
        </div>
      ) : null}

      {/* Снять «скрыт на платформе» — это тоже повышение открытости, поэтому
          платформы гейт пустого видео гасит целиком: половину свитча выключить нельзя. */}
      {access === 'public' && isAdmin ? (
        <PlatformVisibility course={course} disabled={disabled || platformBlocked} />
      ) : null}
    </div>
  )
}
